import { type Dtos } from "@inrev/common";
import { cva } from "class-variance-authority";
import { cn } from "../../../../lib/utils";

type Side = Dtos.Decisioning.ProConKind;
type Weight = Dtos.Decisioning.ProConImportance;

const BARS = [1, 2, 3, 4] as const;

const barVariants = cva("rounded-[1px]", {
	variants: {
		filled: { true: "", false: "bg-bc-default" },
		side: { pro: "", con: "" },
	},
	compoundVariants: [
		{ filled: true, side: "pro", className: "bg-confirm" },
		{ filled: true, side: "con", className: "bg-danger" },
	],
});

export type ImportanceMeterProps = {
	weight: Weight;
	side: Side;
	/** `stacked` fills from the bottom (bucket rail); `rising` is a bar chart (importance picker). */
	variant?: "stacked" | "rising";
	/** Draws every bar in the neutral color, e.g. for an unselected picker option. */
	muted?: boolean;
	className?: string;
};

/** Four bars filled by weight in the side color. Decorative; the label next to it carries the meaning. */
export const ImportanceMeter = ({
	weight,
	side,
	variant = "stacked",
	muted = false,
	className,
}: ImportanceMeterProps) => (
	<span
		aria-hidden
		data-testid="importance-meter"
		data-weight={weight}
		className={cn(
			"flex",
			variant === "stacked" ? "flex-col-reverse gap-px" : "items-end gap-0.5",
			className,
		)}
	>
		{BARS.map((bar) => (
			<span
				key={bar}
				data-filled={!muted && bar <= weight}
				className={cn(
					barVariants({ filled: !muted && bar <= weight, side }),
					variant === "stacked" ? "h-1 w-2.5" : "w-[3px]",
				)}
				style={variant === "rising" ? { height: `${bar * 3 + 2}px` } : undefined}
			/>
		))}
	</span>
);
